import React, { useEffect, useState, useRef } from "react";
import LoadingModal from "../components/Common/LoadingModal";
import LoginModal from "../components/Common/LoginModal";
import "../styles/Admin.css";
import mqtt from "mqtt";

// Import new components
import { AuthProvider, useAuth } from "../components/Admin/Auth/AuthProvider";
import MQTTStatus from "../components/Admin/MQTT/MQTTStatus";
import ControlPanel from "../components/Admin/Controls/ControlPanel";
import OrderFilters from "../components/Admin/Orders/OrderFilters";
import OrderList from "../components/Admin/Orders/OrderList";
import ConfirmationModal from "../components/Admin/Modals/ConfirmationModal";
import PhotoPriceModal from "../components/Admin/Modals/PhotoPriceModal";
import GunPriceModal from "../components/Admin/GunPriceModal";
import OrderHistoryModal from "../components/Admin/OrderHistoryModal";
import SubmissionViewerModal from "../components/Admin/SubmissionViewerModal.tsx";
import NamesListModal from "../components/Admin/Modals/NamesListModal";
import QRModal from "../components/Common/QRModal";

// Import utilities
import { 
  PHOTO_ORDERS_API_URL, 
  GUN_ORDERS_API_URL, 
  EMAIL_ENDPOINT, 
  MQTT_CONFIG, 
  AUTH_ENDPOINTS,
  STORAGE_KEYS 
} from "../components/Admin/Utils/constants";
import { parseOrderData } from "../components/Admin/Utils/orderParser";

const AdminContent = () => {
  const { isAuthenticated, login, getAuthHeaders } = useAuth();
  
  // State management
  const [kvData, setKvData] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState(new Set());
  // eslint-disable-next-line
  const [currentTime, setCurrentTime] = useState(Date.now());
  const [mqttConnected, setMqttConnected] = useState(false);
  const [mqttError, setMqttError] = useState(null);
  const [showGunOrders, setShowGunOrders] = useState(true);
  const [showPhotoOrders, setShowPhotoOrders] = useState(true);
  const [listView, setListView] = useState(() => {
    const saved = localStorage.getItem('adminListView');
    return saved === 'true';
  });
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [showGunPriceModal, setShowGunPriceModal] = useState(false);
  const [showOrderHistoryModal, setShowOrderHistoryModal] = useState(false);
  const [showSubmissionViewerModal, setShowSubmissionViewerModal] = useState(false);
  const [showNamesListModal, setShowNamesListModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrUrl, setQrUrl] = useState('');
  const [currentPrice, setCurrentPrice] = useState(0);
  const [priceLoading, setPriceLoading] = useState(false);
  const [processing, setProcessing] = useState({});
  const [emailedKeys, setEmailedKeys] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.emailedKeys);
    if (!saved) return new Set();
    const keys = JSON.parse(saved).filter(key => {
      const item = localStorage.getItem(`${STORAGE_KEYS.emailTime}${key}`);
      return item && Date.now() - parseInt(item) < 48 * 60 * 60 * 1000;
    });
    return new Set(keys);
  });
  const [printedKeys, setPrintedKeys] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEYS.printedKeys);
    if (!saved) return new Set();
    const keys = JSON.parse(saved).filter(key => {
      const item = localStorage.getItem(`${STORAGE_KEYS.printTime}${key}`);
      return item && Date.now() - parseInt(item) < 48 * 60 * 60 * 1000;
    });
    return new Set(keys);
  });
  const [toast, setToast] = useState(null);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [confirmCallback, setConfirmCallback] = useState(null);
  
  const mqttClient = useRef(null);

  // Toast notification
  const showToast = (msg, type = "info", dur = 3000) => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), dur);
  };

  // Show QR code modal
  const showQRCode = (url) => {
    setQrUrl(url);
    setShowQRModal(true);
  };

  // Copy to clipboard
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showToast("URL copied to clipboard!", "success");
    } catch {
      showToast("Copy failed – unsupported browser", "error");
    }
  };

  // Save printed key
  const savePrintedKey = (key) => {
    const newSet = new Set(printedKeys).add(key);
    setPrintedKeys(newSet);
    localStorage.setItem(STORAGE_KEYS.printedKeys, JSON.stringify([...newSet]));
    localStorage.setItem(`${STORAGE_KEYS.printTime}${key}`, Date.now().toString());
  };

  // Fetch KV data
  const fetchKV = async () => {
    setLoading(true);
    try {
      const [photoRes, gunRes] = await Promise.all([
        fetch(`${PHOTO_ORDERS_API_URL}?list=all`),
        fetch(`${GUN_ORDERS_API_URL}kv`)
      ]);

      const photoData = await photoRes.json();
      const gunData = await gunRes.json();

      setKvData({ ...photoData, ...gunData });
    } catch (err) {
      console.error("Failed to fetch KV data:", err);
    } finally {
      setLoading(false);
    }
  };

  // Delete key with optimistic updates
  const deleteKey = async (key) => {
    // Save the order in case we need to restore it
    const orderToDelete = kvData[key];
    
    // Optimistically remove from UI immediately
    setKvData(prev => {
      const upd = { ...prev };
      delete upd[key];
      return upd;
    });
    
    setExpandedKeys(prev => {
      const n = new Set(prev);
      n.delete(key);
      return n;
    });
    
    setProcessing(p => ({ ...p, [key]: { ...(p[key] || {}), delete: true } }));
    
    try {
      const order = parseOrderData(orderToDelete);
      const isGunOrder = order?.type === 'gun';
      
      const url = isGunOrder
        ? `${GUN_ORDERS_API_URL}kv/${key}`
        : `${PHOTO_ORDERS_API_URL}?key=${encodeURIComponent(key)}`;
      
      await fetch(url, { method: "DELETE" });
      
      showToast("Record deleted.", "success");
    } catch (err) {
      console.error("Failed to delete key:", err);
      // Restore the order if deletion failed
      setKvData(prev => ({ ...prev, [key]: orderToDelete }));
      showToast("Delete failed – order restored.", "error");
    } finally {
      setProcessing(p => ({ ...p, [key]: { ...(p[key] || {}), delete: false } }));
    }
  };

  // Email action
  const handleEmailAction = async (key, email, zipUrl) => {
    if (!email || !zipUrl) {
      showToast("Missing email or ZIP URL", "error");
      return;
    }
    
    setProcessing(p => ({ ...p, [key]: { ...(p[key] || {}), email: true } }));
    
    try {
      // Send email
      const res = await fetch(EMAIL_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, zip_url: zipUrl }),
      });
      
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await res.json();
      
      // Save to order history
      const orderData = parseOrderData(kvData[key]);
      const historyRes = await fetch(AUTH_ENDPOINTS.orderHistory, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Auth-Guid": "17225c0b-2167-46c8-bba9-506f5b391813"
        },
        body: JSON.stringify({
          id: key,
          data: orderData
        })
      });
      
      if (!historyRes.ok) throw new Error(`Failed to save order history`);
      
      showToast("Email sent and order saved!", "success");
      
      const newSet = new Set(emailedKeys).add(key);
      setEmailedKeys(newSet);
      localStorage.setItem(STORAGE_KEYS.emailedKeys, JSON.stringify([...newSet]));
      localStorage.setItem(`${STORAGE_KEYS.emailTime}${key}`, Date.now().toString());
    } catch (err) {
      console.error("Failed to send email:", err);
      showToast("Email failed – see console.", "error");
    } finally {
      setProcessing(p => ({ ...p, [key]: { ...(p[key] || {}), email: false } }));
    }
  };

  // Update email
  const updateEmail = (key, newEmail) => {
    setKvData(prev => {
      try {
        const parsed = JSON.parse(prev[key] ?? "{}");
        const updated = { ...parsed, email: newEmail };
        return { ...prev, [key]: JSON.stringify(updated) };
      } catch {
        return prev;
      }
    });
  };

  // Update notes
  const updateNotes = (key, newNotes) => {
    setKvData(prev => {
      try {
        const parsed = JSON.parse(prev[key] ?? "{}");
        const updated = { ...parsed, notes: newNotes };
        return { ...prev, [key]: JSON.stringify(updated) };
      } catch {
        return prev;
      }
    });
  };

  // Toggle expand
  const toggleExpand = (key) => {
    setExpandedKeys(prev => {
      const n = new Set(prev);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  };

  // MQTT setup
  useEffect(() => {
    const handleEmailAction = async (key, email, zipUrl) => {
      if (!email || !zipUrl) {
        showToast("Missing email or ZIP URL", "error");
        return;
      }
      
      setProcessing(p => ({ ...p, [key]: { ...(p[key] || {}), email: true } }));
      
      try {
        const res = await fetch(EMAIL_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, zip_url: zipUrl }),
        });
        
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        await res.json();
        
        showToast("Email sent!", "success");
        
        const newSet = new Set(emailedKeys).add(key);
        setEmailedKeys(newSet);
        localStorage.setItem(STORAGE_KEYS.emailedKeys, JSON.stringify([...newSet]));
        localStorage.setItem(`${STORAGE_KEYS.emailTime}${key}`, Date.now().toString());
      } catch (err) {
        console.error("Failed to send email:", err);
        showToast("Email failed – see console.", "error");
      } finally {
        setProcessing(p => ({ ...p, [key]: { ...(p[key] || {}), email: false } }));
      }
    };

    const addNewOrderToList = async (data) => {
      if (!data.key) return;
      
      try {
        if (data.topic === 'gun_orders') {
          const res = await fetch(`${GUN_ORDERS_API_URL}kv/${data.key}`);
          if (res.ok) {
            const orderDetails = await res.json();
            setKvData(prev => ({ ...prev, [data.key]: JSON.stringify(orderDetails) }));
          }
        } else {
          const res = await fetch(`${PHOTO_ORDERS_API_URL}?key=${encodeURIComponent(data.key)}`);
          if (res.ok) {
            const orderValue = await res.text();
            const order = parseOrderData(orderValue);
            setKvData(prev => ({ ...prev, [data.key]: orderValue }));

            if (order?.total === 0 && order?.email && order?.url) {
              await handleEmailAction(data.key, order.email, order.url);
            }
          }
        }
      } catch (err) {
        console.error(`Failed to fetch order ${data.key}:`, err);
      }
    };

    const connectMQTT = () => {
      try {
        mqttClient.current = mqtt.connect(MQTT_CONFIG.brokerUrl, {
          clientId: MQTT_CONFIG.clientId,
          username: MQTT_CONFIG.username,
          password: MQTT_CONFIG.password,
          clean: true,
          connectTimeout: 10000,
          reconnectPeriod: 5000,
        });

        mqttClient.current.on("connect", () => {
          setMqttConnected(true);
          setMqttError(null);
          MQTT_CONFIG.topics.forEach(topic => {
            mqttClient.current.subscribe(
              topic,
              (err) => err && setMqttError(`Failed to subscribe to ${topic}`)
            );
          });
        });

        mqttClient.current.on("message", (topic, message) => {
          if (!MQTT_CONFIG.topics.includes(topic)) return;
          console.log(`MQTT message from ${topic}:`, message.toString());
          
          try {
            if (topic === 'gun_orders') {
              const orderId = message.toString();
              if (orderId) {
                addNewOrderToList({
                  key: orderId,
                  topic: 'gun_orders'
                });
              }
            } else {
              const data = JSON.parse(message.toString());
              if (data.action === "created") {
                addNewOrderToList({...data, topic});
              }
            }
          } catch (e) {
            console.error("Error processing MQTT message:", e);
          }
        });

        mqttClient.current.on("error", () => setMqttConnected(false));
        mqttClient.current.on("offline", () => setMqttConnected(false));
      } catch {
        setMqttError("Failed to connect to live updates");
      }
    };

    connectMQTT();
    
    return () => {
      if (mqttClient.current?.end) {
        try {
          mqttClient.current.end(true);
        } catch (e) {
          console.warn("MQTT end() failed:", e);
        }
      }
    };
  }, [emailedKeys]);

  // Initial fetch and timer
  useEffect(() => {
    fetchKV();
  }, []);

  useEffect(() => {
    const id = setInterval(() => setCurrentTime(Date.now()), 60000);
    return () => clearInterval(id);
  }, []);

  // Handle delete all images
  const handleDeleteAllImages = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers['X-Auth-Guid']) {
        throw new Error("No auth token found");
      }

      const response = await fetch(
        AUTH_ENDPOINTS.deleteAllImages,
        {
          method: "DELETE",
          headers,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const message = `Deleted ${result.deletedCount} files, ${result.failedCount} failed`;
      showToast(message, "success");
      return true;
    } catch (err) {
      console.error("Delete all images error:", err);
      showToast(`Error: ${err.message}`, "error");
      return false;
    }
  };

  // Handle refresh pages
  const handleRefreshPages = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers['Authorization']) {
        throw new Error("No auth token found");
      }

      const response = await fetch(
        AUTH_ENDPOINTS.refreshPages,
        {
          method: "POST",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      showToast(result.message || "Pages refreshed successfully", "success");
    } catch (err) {
      showToast(`Failed to refresh pages: ${err.message}`, "error");
      console.error("Refresh pages error:", err);
    }
  };

  // Handle delete all orders
  const handleDeleteAllOrders = async () => {
    const sortedEntries = Object.entries(kvData).sort(([ka, va], [kb, vb]) => {
      const a = parseOrderData(va);
      const b = parseOrderData(vb);
      if (a && b) return new Date(a.timestamp) - new Date(b.timestamp);
      if (a && !b) return -1;
      if (!a && b) return 1;
      return ka.localeCompare(kb);
    });

    const orderCount = sortedEntries.length;
    setConfirmMessage(`Delete ALL ${orderCount} orders? This cannot be undone.`);
    setConfirmCallback(() => async (confirmed) => {
      if (!confirmed) return;

      try {
        setIsDeletingAll(true);
        const deletePromises = sortedEntries.map(([key]) => {
          const order = parseOrderData(kvData[key]);
          const isGunOrder = order?.type === "gun";
          const url = isGunOrder
            ? `${GUN_ORDERS_API_URL}kv/${key}`
            : `${PHOTO_ORDERS_API_URL}?key=${encodeURIComponent(key)}`;

          return fetch(url, { method: "DELETE" });
        });

        await Promise.all(deletePromises);
        setKvData({});
        setExpandedKeys(new Set());
        showToast(`Deleted ${orderCount} orders`, "success");
      } catch (err) {
        console.error("Failed to delete orders:", err);
        showToast("Some orders failed to delete - see console", "error");
      } finally {
        setIsDeletingAll(false);
        setLoading(false);
      }
    });
    setShowConfirmModal(true);
  };

  // Handle photo price update
  const handlePhotoPriceUpdate = async () => {
    setPriceLoading(true);
    try {
      const headers = getAuthHeaders();
      if (!headers['Authorization']) {
        throw new Error("No auth token found");
      }

      const res = await fetch(
        `${AUTH_ENDPOINTS.pricing}/photos`,
        {
          method: "PUT",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ value: currentPrice }),
        }
      );
      
      if (!res.ok) throw new Error("Failed to update price");
      const data = await res.json();
      showToast(data.message || "Price updated successfully", "success");
      setShowPriceModal(false);

      // Refresh pages after price update
      const refreshRes = await fetch(
        AUTH_ENDPOINTS.refreshPages,
        {
          method: "POST",
          headers: {
            ...headers,
            "Content-Type": "application/json",
          },
        }
      );
      
      if (refreshRes.ok) {
        showToast("Pages refreshed successfully", "success");
      } else {
        showToast("Price updated but failed to refresh pages", "warning");
      }
    } catch (err) {
      showToast(`Failed to update price: ${err.message}`, "error");
    } finally {
      setPriceLoading(false);
    }
  };

  // Load current photo price
  const loadCurrentPhotoPrice = async () => {
    setPriceLoading(true);
    try {
      const headers = getAuthHeaders();
      if (!headers['Authorization']) {
        throw new Error("No auth token found");
      }

      const res = await fetch(
        `${AUTH_ENDPOINTS.pricing}?ids=photos`,
        { headers }
      );
      
      if (!res.ok) throw new Error("Failed to fetch price");
      const { data } = await res.json();
      setCurrentPrice(data.photos.toString());
    } catch (err) {
      showToast(`Failed to get price: ${err.message}`, "error");
    } finally {
      setPriceLoading(false);
    }
  };

  // Handle confirmation
  const handleConfirm = (result) => {
    setShowConfirmModal(false);
    if (confirmCallback) {
      confirmCallback(result);
    }
  };

  // Handle downloading all gallery images
  const handleDownloadGalleryImages = async (directoryHandle, onProgress) => {
    const WORKER_URL = "https://r2-image-service.t9e2i3kal.workers.dev";
    
    try {
      // Fetch all images from the gallery
      const response = await fetch(`${WORKER_URL}/api/media`);
      const json = await response.json();
      
      if (!json.success) {
        throw new Error(json.error || "Failed to fetch gallery images");
      }
      
      const images = json.data;
      const totalImages = images.length;
      
      if (totalImages === 0) {
        showToast("No images found in gallery", "info");
        return;
      }
      
      let downloadedCount = 0;
      
      // Download each image
      for (let i = 0; i < images.length; i++) {
        const image = images[i];
        
        try {
          // Fetch the image data
          const imageResponse = await fetch(image.url);
          if (!imageResponse.ok) {
            console.error(`Failed to download ${image.name}: ${imageResponse.status}`);
            continue;
          }
          
          const blob = await imageResponse.blob();
          
          // Create file in selected directory
          const fileHandle = await directoryHandle.getFileHandle(image.name, { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(blob);
          await writable.close();
          
          downloadedCount++;
          
          // Update progress
          if (onProgress) {
            onProgress((downloadedCount / totalImages) * 100);
          }
          
        } catch (error) {
          console.error(`Error downloading ${image.name}:`, error);
        }
      }
      
      showToast(`Downloaded ${downloadedCount} of ${totalImages} images`, "success");
      
    } catch (error) {
      console.error("Gallery download failed:", error);
      throw new Error(`Failed to download gallery images: ${error.message}`);
    }
  };

  // Handle show photo price modal
  const handleShowPhotoPriceModal = async () => {
    setShowPriceModal(true);
    await loadCurrentPhotoPrice();
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-page">
        <LoginModal
          show={true}
          onSubmit={login}
          error={""}
          loading={false}
          title="Enter Password"
        />
      </div>
    );
  }

  return (
    <div className="admin-page">
      {toast && <div className={`toast toast-${toast.type}`}>{toast.msg}</div>}

      <ConfirmationModal
        show={showConfirmModal}
        message={confirmMessage}
        onConfirm={handleConfirm}
      />

      <div className="admin-container">
        <ControlPanel
          onShowGunPriceModal={() => setShowGunPriceModal(true)}
          onShowPhotoPriceModal={handleShowPhotoPriceModal}
          priceLoading={priceLoading}
          onShowOrderHistory={() => setShowOrderHistoryModal(true)}
          onDeleteAllOrders={handleDeleteAllOrders}
          orderCount={Object.keys(kvData).length}
          isDeletingAll={isDeletingAll}
          onDeleteAllImages={() => {
            setConfirmMessage("Delete ALL gallery images? This cannot be undone.");
            setConfirmCallback(() => async (confirmed) => {
              if (confirmed) {
                await handleDeleteAllImages();
              }
            });
            setShowConfirmModal(true);
          }}
          onRefreshPages={() => {
            setConfirmMessage("This button will refresh all running instances. Are you sure you want to proceed?");
            setConfirmCallback(() => async (confirmed) => {
              if (confirmed) {
                await handleRefreshPages();
              }
            });
            setShowConfirmModal(true);
          }}
          processing={Object.values(processing).some(p => p.refresh)}
          onShowSubmissionViewer={() => setShowSubmissionViewerModal(true)}
          listView={listView}
          onToggleListView={() => {
            const newView = !listView;
            setListView(newView);
            localStorage.setItem('adminListView', newView.toString());
          }}
          onDownloadGalleryImages={handleDownloadGalleryImages}
          onShowNamesList={() => setShowNamesListModal(true)}
        />

          <MQTTStatus connected={mqttConnected} error={mqttError} />

        <OrderFilters
          showGunOrders={showGunOrders}
          showPhotoOrders={showPhotoOrders}
          onToggleGunOrders={() => setShowGunOrders(!showGunOrders)}
          onTogglePhotoOrders={() => setShowPhotoOrders(!showPhotoOrders)}
          gunOrderCount={Object.entries(kvData).filter(([_, v]) => {
            const order = parseOrderData(v);
            return order?.type === "gun";
          }).length}
          photoOrderCount={Object.entries(kvData).filter(([_, v]) => {
            const order = parseOrderData(v);
            return order?.type === "photo";
          }).length}
        />

        {loading ? (
          isDeletingAll ? (
            <LoadingModal message="Deleting All Orders..." />
          ) : (
            <LoadingModal message="Loading orders..." />
          )
        ) : Object.keys(kvData).length === 0 ? (
          <div className="center">
            <img
              src="/images/tony.gif"
              alt="No orders found"
              style={{ maxWidth: "100%", height: "auto" }}
            />
          </div>
        ) : (
          <OrderList
            orders={kvData}
            expandedKeys={expandedKeys}
            processing={processing}
            emailedKeys={emailedKeys}
            printedKeys={printedKeys}
            showGunOrders={showGunOrders}
            showPhotoOrders={showPhotoOrders}
            onToggleExpand={toggleExpand}
            onDelete={deleteKey}
            onEmail={handleEmailAction}
            onPrint={savePrintedKey}
            onCopyUrl={copyToClipboard}
            onUpdateEmail={updateEmail}
            onUpdateNotes={updateNotes}
            onShowQRCode={showQRCode}
          />
        )}
      </div>

      <GunPriceModal
        show={showGunPriceModal}
        onClose={() => setShowGunPriceModal(false)}
        onSave={() => showToast("Gun prices updated successfully", "success")}
      />

      <OrderHistoryModal
        show={showOrderHistoryModal}
        onClose={() => setShowOrderHistoryModal(false)}
      />

      <SubmissionViewerModal
        show={showSubmissionViewerModal}
        onClose={() => setShowSubmissionViewerModal(false)}
      />

      <NamesListModal
        show={showNamesListModal}
        onClose={() => setShowNamesListModal(false)}
      />

      <PhotoPriceModal
        show={showPriceModal}
        onClose={() => setShowPriceModal(false)}
        onUpdate={handlePhotoPriceUpdate}
        currentPrice={currentPrice}
        onPriceChange={setCurrentPrice}
        loading={priceLoading}
      />

      <QRModal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        url={qrUrl}
        showPrintButton={true}
      />
    </div>
  );
};

const AdminPage = () => {
  return (
    <AuthProvider>
      <AdminContent />
    </AuthProvider>
  );
};

export default AdminPage;
